import moment from "moment";

/**
 * Aggregate Mastersheet items into a monthly "Rent Billed" series.
 *
 * Each item is treated as a unit with a monthly rent (basic_rent / basicRent)
 * billable while a lease is active (lease_start <= month <= lease_end).
 *
 * @param {Array} items   raw v2/master_sheet response items
 * @param {Object} range  { start, end } as "YYYY-MM-DD" — both optional
 * @returns {{ categories: string[], total: number, monthly: number[] }}
 */
export const aggregateMastersheetRent = (items, range) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { categories: [], monthly: [], total: 0, months: [] };
  }

  const parseRent = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return v;
    const cleaned = String(v).replace(/[^\d.-]/g, "");
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  };

  const rentOf = (item) =>
    parseRent(item.basic_rent ?? item.basicRent ?? item.rent);

  const startOf = (item) =>
    item.lease_start
      ? moment(item.lease_start, ["YYYY-MM-DD", moment.ISO_8601])
      : null;

  const endOf = (item) =>
    item.lease_end
      ? moment(item.lease_end, ["YYYY-MM-DD", moment.ISO_8601])
      : moment();

  let windowStart =
    range && range.start
      ? moment(range.start, "YYYY-MM-DD").startOf("month")
      : null;
  let windowEnd =
    range && range.end
      ? moment(range.end, "YYYY-MM-DD").endOf("month")
      : moment().endOf("month");

  if (!windowStart) {
    let earliest = null;
    items.forEach((item) => {
      const s = startOf(item);
      if (s && s.isValid() && (!earliest || s.isBefore(earliest))) {
        earliest = s.clone();
      }
    });
    windowStart = earliest
      ? earliest.startOf("month")
      : moment().subtract(11, "months").startOf("month");
  }

  if (windowEnd.isBefore(windowStart)) {
    windowEnd = windowStart.clone().endOf("month");
  }

  const months = [];
  const cursor = windowStart.clone();
  while (cursor.isSameOrBefore(windowEnd, "month")) {
    months.push(cursor.clone());
    cursor.add(1, "month");
    if (months.length > 36) break;
  }

  const monthly = months.map(() => 0);

  items.forEach((item) => {
    const rent = rentOf(item);
    if (!rent) return;
    const s = startOf(item);
    const e = endOf(item);
    if (!s || !s.isValid()) return;

    months.forEach((m, idx) => {
      const active = m.isSameOrAfter(s, "month") && m.isSameOrBefore(e, "month");
      if (active) monthly[idx] += rent;
    });
  });

  const categories = months.map((m) => m.format("MMM YYYY"));
  const total = monthly.reduce((a, b) => a + b, 0);

  return { categories, monthly, total, months };
};
